import type {
  TransientAuthorizationRequest,
  TransientAuthorizationRequestStore,
} from './oauth-authorization.js';

const STORAGE_KEY_PREFIX = 'infrahub.oauth.pending.';

type SessionStoragePort = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type UnknownRecord = Readonly<Record<string, unknown>>;

function storageKey(state: string): string {
  return `${STORAGE_KEY_PREFIX}${state}`;
}

function parseStoredRequest(value: string): TransientAuthorizationRequest {
  const parsed = JSON.parse(value) as unknown;
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Stored OAuth authorization request must be an object.');
  }

  const record = parsed as UnknownRecord;
  const state = record['state'];
  const codeVerifier = record['codeVerifier'];
  const redirectUri = record['redirectUri'];
  const createdAt = record['createdAt'];

  if (
    typeof state !== 'string' ||
    state.length === 0 ||
    typeof codeVerifier !== 'string' ||
    codeVerifier.length === 0 ||
    typeof redirectUri !== 'string' ||
    redirectUri.length === 0 ||
    typeof createdAt !== 'number' ||
    !Number.isFinite(createdAt)
  ) {
    throw new Error('Stored OAuth authorization request is malformed.');
  }

  return { state, codeVerifier, redirectUri, createdAt };
}

export class SessionStorageAuthorizationRequestStore implements TransientAuthorizationRequestStore {
  readonly #storage: SessionStoragePort;

  constructor(storage: SessionStoragePort) {
    this.#storage = storage;
  }

  save(request: TransientAuthorizationRequest): void {
    this.#storage.setItem(storageKey(request.state), JSON.stringify(request));
  }

  consume(state: string): TransientAuthorizationRequest | undefined {
    const key = storageKey(state);
    const stored = this.#storage.getItem(key);
    this.#storage.removeItem(key);

    if (stored === null) {
      return undefined;
    }

    const request = parseStoredRequest(stored);
    if (request.state !== state) {
      throw new Error('Stored OAuth authorization request state does not match its storage key.');
    }
    return request;
  }
}

export function createBrowserAuthorizationRequestStore(): SessionStorageAuthorizationRequestStore {
  return new SessionStorageAuthorizationRequestStore(globalThis.sessionStorage);
}
